import { computed, ref, shallowRef, watch, type ComputedRef, type Ref } from 'vue'

import type { OverviewFrequency, OverviewGroup, OverviewSeriesMap } from '../models/overview.types'
import { aggregateOverviewSeries } from '../service/aggregateOverviewSeries'
import { isRangeEndToday } from '../service/isRangeEndToday'
import { mergeForecastIntoActual } from '../service/mergeForecastIntoActual'
import {
  availableOverviewPlatforms,
  mergeOverviewSeries,
  normalizeConversionRows,
  overviewDateKey,
  overviewDateRange,
  selectDefaultOverviewMetricFields,
  SUMMARY_OVERVIEW_DEFAULT_METRICS,
} from '../service/summaryOverview'

import { useWorkspace } from '@/features/account'
import {
  mapColumns,
  useMetricCatalog,
  useReportData,
  useReportSchedules,
  type ReportColumn,
  type ReportDataState,
  type ReportFilter,
  type ReportRow,
  type ReportSpec,
  type ScheduleTab,
} from '@/features/reports'
import type { SelectOption } from '@/shared/ui/select'

export interface UseSummaryOverview {
  state: ComputedRef<ReportDataState>
  error: ComputedRef<unknown | null>
  series: ComputedRef<OverviewSeriesMap | null>
  forecastSeries: ComputedRef<OverviewSeriesMap | null>
  metrics: ComputedRef<ReportColumn[]>
  selectedMetricFields: Ref<string[]>
  activeMetricFields: Ref<string[]>
  selectedGroup: Ref<OverviewGroup>
  selectedFrequency: Ref<OverviewFrequency>
  displayedFrequency: ComputedRef<OverviewFrequency>
  selectedCampaign: Ref<string | null>
  selectedChannels: Ref<string[]>
  forecastEnabled: Ref<boolean>
  groupOptions: ComputedRef<SelectOption<OverviewGroup>[]>
  frequencyOptions: SelectOption<OverviewFrequency>[]
  campaignOptions: ComputedRef<SelectOption<string>[]>
  channelOptions: Ref<SelectOption<string>[]>
  platforms: ComputedRef<string[]>
  activePlatform: Ref<string>
  hasForecast: ComputedRef<boolean>
  reload: () => Promise<void>
}

function tab(value: unknown): ScheduleTab | null {
  return typeof value === 'object' && value !== null ? (value as ScheduleTab) : null
}

function withPlatform(rows: readonly ReportRow[], field: string | null): ReportRow[] {
  return rows.map((row) => ({ ...row, platform: field ? (row[field] ?? 'all') : 'all' }))
}

export function useSummaryOverview(defaultPostData: ComputedRef<string>): UseSummaryOverview {
  const workspace = useWorkspace()
  const schedules = useReportSchedules()
  const catalog = useMetricCatalog()
  const budgetSchedule = computed(() => schedules.getScheduleByReportId(6))
  const conversionSchedule = computed(() => schedules.getScheduleByReportId(50))
  const productSchedule = computed(() => schedules.getScheduleByReportId(17))
  const budgetTabs = computed(() => budgetSchedule.value?.additional_info?.tabs ?? {})
  const conversionTab = computed(() =>
    tab(conversionSchedule.value?.additional_info?.tabs?.dashboard_conversion_event_data),
  )
  const shopifyTab = computed(() =>
    tab(productSchedule.value?.additional_info?.tabs?.['Shopify Summary']),
  )
  const budgetMode = computed(() =>
    Object.values(budgetTabs.value).some(
      (value) => tab(value)?.partition_name === 'budget_allocation',
    ),
  )

  const selectedGroup = ref<OverviewGroup>(budgetMode.value ? 'overall' : 'platform')
  const selectedFrequency = ref<OverviewFrequency>('date')
  const selectedCampaign = ref<string | null>(null)
  const selectedChannels = ref<string[]>([])
  const forecastEnabled = ref(false)
  const activePlatform = ref('all')
  const selectedMetricFields = ref<string[]>([])
  const activeMetricFields = ref<string[]>([])
  const channelOptions = ref<SelectOption<string>[]>([])

  const customGroups = computed(() => {
    const value = workspace.getAttribute('custom_grouping')?.value
    if (!Array.isArray(value)) return []
    return value.flatMap((item) => {
      if (typeof item !== 'object' || item === null) return []
      const group = item as { entity_type?: unknown; slot_name?: unknown }
      return group.entity_type === 'campaign' && typeof group.slot_name === 'string'
        ? [group.slot_name]
        : []
    })
  })
  const campaignOptions = computed(() =>
    customGroups.value.map((field) => ({ value: field, label: catalog.metricLabel(field) })),
  )
  const groupOptions = computed<SelectOption<OverviewGroup>[]>(() => {
    const base: OverviewGroup[] = conversionTab.value
      ? ['overall', 'marketing_channel', 'platform', 'platform_device_category']
      : ['overall', 'marketing_channel', 'campaign_group']
    if (campaignOptions.value.length && !base.includes('campaign_group'))
      base.push('campaign_group')
    return base.map((value) => ({ value, label: catalog.metricLabel(value) }))
  })
  const frequencyOptions: SelectOption<OverviewFrequency>[] = [
    { value: 'date', label: 'summary.overview.frequency.daily' },
    { value: 'week', label: 'summary.overview.frequency.weekly' },
    { value: 'month_of_year', label: 'summary.overview.frequency.monthly' },
    { value: 'year', label: 'summary.overview.frequency.yearly' },
  ]

  /** Report 6 `overview` tab's `highlighted_metrics` — the cards the rail opens on (v3 parity). */
  const highlightedMetricFields = computed(
    () => tab(budgetTabs.value.overview)?.highlighted_metrics?.filter(Boolean) ?? [],
  )
  const budgetMetricFields = computed(() => {
    const fields = Object.values(budgetTabs.value).flatMap((value) => tab(value)?.columns ?? [])
    const numeric = fields.filter((field) => catalog.getColumn(field)?.numeric)
    return [...new Set([...highlightedMetricFields.value, ...numeric])].filter(Boolean)
  })
  const mode = computed<'budget' | 'conversion'>(() =>
    budgetMode.value && !['platform', 'platform_device_category'].includes(selectedGroup.value)
      ? 'budget'
      : 'conversion',
  )
  const groupField = computed(() => {
    if (selectedGroup.value === 'overall') return null
    if (selectedGroup.value === 'campaign_group') return selectedCampaign.value
    return selectedGroup.value
  })
  const filters = computed<ReportFilter[]>(() => {
    if (
      selectedGroup.value !== 'marketing_channel' ||
      selectedChannels.value.length === 0 ||
      selectedChannels.value.length === channelOptions.value.length
    )
      return []
    return [{ field: 'marketing_channel', match_mode: 'in', value: selectedChannels.value }]
  })
  const dimensions = computed(() => [
    selectedFrequency.value,
    ...(selectedFrequency.value === 'month_of_year' ? ['year'] : []),
    ...(groupField.value ? [groupField.value] : []),
  ])
  const actualSpec = computed<ReportSpec | null>(() => {
    if (mode.value === 'budget' && budgetMode.value)
      return {
        report_id: 6,
        partition_name: 'budget_allocation',
        dimensions: dimensions.value,
        metrics: budgetMetricFields.value.length
          ? budgetMetricFields.value
          : [...SUMMARY_OVERVIEW_DEFAULT_METRICS],
        filters: filters.value,
        add_compare: true,
        pagination_enabled: false,
        defaultPostData: defaultPostData.value,
      }
    if (!conversionTab.value?.partition_name) return null
    return {
      report_id: 50,
      partition_name: conversionTab.value.partition_name,
      dimensions: [...dimensions.value, 'event_name', 'is_key_event'],
      add_compare: true,
      pagination_enabled: false,
      defaultPostData: defaultPostData.value,
    }
  })
  const forecastSpec = computed<ReportSpec | null>(() =>
    mode.value === 'budget' && isRangeEndToday({ queryString: defaultPostData.value })
      ? {
          report_id: 6,
          partition_name: 'budget_allocation_forecast',
          dimensions: dimensions.value,
          ...(actualSpec.value?.metrics ? { metrics: actualSpec.value.metrics } : {}),
          filters: filters.value,
          add_compare: true,
          pagination_enabled: false,
        }
      : null,
  )
  const shopifySpec = computed<ReportSpec | null>(() =>
    mode.value === 'budget' && shopifyTab.value
      ? {
          report_id: 17,
          partition_name: 'shopify_summary_daily',
          dimensions: dimensions.value,
          ...(shopifyTab.value.metrics ? { metrics: shopifyTab.value.metrics } : {}),
          add_compare: true,
          pagination_enabled: false,
          defaultPostData: defaultPostData.value,
        }
      : null,
  )
  const actual = useReportData(actualSpec)
  const forecast = useReportData(forecastSpec)
  const shopify = useReportData(shopifySpec)

  const normalized = computed(() =>
    mode.value === 'conversion'
      ? normalizeConversionRows(actual.rows.value, selectedGroup.value, selectedFrequency.value)
      : {
          rows: withPlatform(actual.rows.value, groupField.value),
          metrics: actualSpec.value?.metrics ?? [],
        },
  )
  const actualSeries = computed(() =>
    aggregateOverviewSeries({
      rows: normalized.value.rows,
      metrics: normalized.value.metrics,
      group: selectedGroup.value,
      dateKeyOf: (row) => overviewDateKey(row, selectedFrequency.value),
      dateRange: overviewDateRange(defaultPostData.value),
      responseTotals: actual.response.value,
    }),
  )
  const shopifySeries = computed(() =>
    aggregateOverviewSeries({
      rows: withPlatform(shopify.rows.value, null),
      metrics: shopifySpec.value?.metrics ?? [],
      group: 'overall',
      dateKeyOf: (row) => overviewDateKey(row, selectedFrequency.value),
      dateRange: overviewDateRange(defaultPostData.value),
      responseTotals: shopify.response.value,
    }),
  )
  const rawSeries = computed(() => mergeOverviewSeries(actualSeries.value, shopifySeries.value))
  const rawForecastSeries = computed(() =>
    aggregateOverviewSeries({
      rows: withPlatform(forecast.rows.value, groupField.value),
      metrics: forecastSpec.value?.metrics ?? [],
      group: selectedGroup.value,
      dateKeyOf: (row) => overviewDateKey(row, selectedFrequency.value),
      responseTotals: forecast.response.value,
    }),
  )
  const rawMergedForecastSeries = computed(() =>
    rawForecastSeries.value && rawSeries.value
      ? mergeForecastIntoActual(rawForecastSeries.value, rawSeries.value, defaultPostData.value)
      : rawForecastSeries.value,
  )
  const settledPresentation = shallowRef<{
    series: OverviewSeriesMap | null
    forecastSeries: OverviewSeriesMap | null
    frequency: OverviewFrequency
  } | null>(null)

  // Report responses remain available while their replacement request is loading. Snapshot the
  // last response-backed presentation so a new grouping/frequency cannot reinterpret those rows
  // and so the ready subtree keeps its height and focus until the replacement response settles.
  watch(
    () => [actual.response.value, forecast.response.value, shopify.response.value] as const,
    () => {
      if (actual.state.value !== 'ready') return
      settledPresentation.value = {
        series: rawSeries.value,
        forecastSeries: rawMergedForecastSeries.value,
        frequency: selectedFrequency.value,
      }
    },
    { immediate: true, flush: 'post' },
  )
  watch(actual.state, (state) => {
    if (state !== 'loading' && state !== 'ready') settledPresentation.value = null
  })

  const series = computed(() =>
    actual.state.value === 'loading' && settledPresentation.value
      ? settledPresentation.value.series
      : rawSeries.value,
  )
  const forecastSeries = computed(() =>
    actual.state.value === 'loading' && settledPresentation.value
      ? settledPresentation.value.forecastSeries
      : rawMergedForecastSeries.value,
  )
  const displayedFrequency = computed(() =>
    actual.state.value === 'loading' && settledPresentation.value
      ? settledPresentation.value.frequency
      : selectedFrequency.value,
  )
  const metricFields = computed(() => [
    ...new Set(Object.values(series.value ?? {}).map((item) => item.metric)),
  ])
  const metrics = computed(() => mapColumns(metricFields.value, { getColumn: catalog.getColumn }))
  const platforms = computed(() => availableOverviewPlatforms(series.value))
  const hasForecast = computed(() => Object.keys(forecastSeries.value ?? {}).length > 0)

  watch(
    metrics,
    (next) => {
      const available = new Set(next.map((metric) => metric.field))
      selectedMetricFields.value = selectedMetricFields.value.filter((field) =>
        available.has(field),
      )
      if (!selectedMetricFields.value.length)
        selectedMetricFields.value = selectDefaultOverviewMetricFields(
          next,
          highlightedMetricFields.value,
        )
      activeMetricFields.value = activeMetricFields.value.filter((field) => available.has(field))
      if (!activeMetricFields.value.length)
        activeMetricFields.value = selectedMetricFields.value.slice(0, 2)
    },
    { immediate: true },
  )
  watch(
    platforms,
    (next) => {
      if (!next.includes(activePlatform.value)) activePlatform.value = next[0] ?? 'all'
    },
    { immediate: true },
  )
  watch(hasForecast, (available) => {
    if (!available) forecastEnabled.value = false
  })
  watch(budgetMode, (available) => {
    if (!available && selectedGroup.value === 'overall') selectedGroup.value = 'platform'
  })
  watch(
    campaignOptions,
    (next) => {
      if (selectedGroup.value === 'campaign_group' && !selectedCampaign.value) {
        selectedCampaign.value = next[0]?.value ?? null
      }
    },
    { immediate: true },
  )
  watch(
    budgetSchedule,
    async (schedule) => {
      if (!schedule) return
      const values =
        schedules.dropdownableDimensions(schedule.id, 'marketing_channel')?.values ??
        (await schedules.loadDropdownValues(schedule.id, 'marketing_channel'))
      channelOptions.value = values.map((value) => ({
        value,
        label: catalog.metricLabel(value),
        meta: { visual: { platform: value } },
      }))
      selectedChannels.value = values
    },
    { immediate: true },
  )

  return {
    state: computed(() => (actual.state.value === 'idle' ? 'loading' : actual.state.value)),
    error: computed(() => actual.error.value),
    series,
    forecastSeries,
    metrics,
    selectedMetricFields,
    activeMetricFields,
    selectedGroup,
    selectedFrequency,
    displayedFrequency,
    selectedCampaign,
    selectedChannels,
    forecastEnabled,
    groupOptions,
    frequencyOptions,
    campaignOptions,
    channelOptions,
    platforms,
    activePlatform,
    hasForecast,
    reload: async () => {
      await Promise.all([actual.reload(), forecast.reload(), shopify.reload()])
    },
  }
}
